// wagmi-latest adapter hooks — wagmi 3.x useConnection + useSendTx
// These hooks MUST be called inside wagmi-latest's Provider (React rules of hooks)

import { useCallback, useMemo } from 'react';
import { useAccount, useConfig, useConnect, useDisconnect, useSendTransaction, useSwitchChain, useWriteContract } from 'wagmi';
import { getConnection, readContract, waitForTransactionReceipt } from 'wagmi/actions';
import { sendEvmTransaction, approveEvmFeeSpend } from './senders.ts';
import type { WagmiSendTx, WagmiWriteContract, WaitForReceipt, ReadAllowance } from './senders.ts';

const ERC20_ALLOWANCE_ABI = [{
  type: 'function', name: 'allowance', stateMutability: 'view',
  inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
  outputs: [{ name: '', type: 'uint256' }],
}] as const;
import { clearWcSessions } from './cleanup.ts';
import { ok, err } from '@shared/types/result.ts';
import type { ConnectionHookResult, SendTxHookResult } from '../../types.ts';
import type { TxRequest, TxResult } from '../../txRequest.ts';

// --- useConnection: account state + connect/disconnect ---

export const useConnection = (): ConnectionHookResult => {
  const { address, isConnected, isReconnecting, chainId, connector } = useAccount();
  const { connectors, connectAsync } = useConnect();
  const { disconnectAsync } = useDisconnect();

  // Always open WalletConnect modal — it shows a large wallet list
  // (MetaMask, Trust, Rainbow, etc.) with QR code and deep links.
  // Previous behavior auto-picked injected MetaMask on desktop, bypassing the list.
  const openModal = useCallback(async (): Promise<void> => {
    const wcConnector = connectors.find((c) => c.id === 'walletConnect');
    const target = wcConnector ?? connectors[0];

    if (!target) return;

    // WC mobile fix: restart relay WebSocket when browser resumes from background.
    // On mobile, the WS disconnects while the user is in the wallet app approving.
    let removeListener: (() => void) | null = null;
    if (target.id === 'walletConnect') {
      const onVisible = (): void => {
        if (document.visibilityState !== 'visible') return;
        (target as { getProvider?: () => Promise<unknown> }).getProvider?.()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .then((p: any) => p?.client?.core?.relayer?.restartTransport?.())
          .catch(() => {});
      };
      document.addEventListener('visibilitychange', onVisible);
      removeListener = () => document.removeEventListener('visibilitychange', onVisible);
    }

    try {
      await connectAsync({ connector: target });
    } catch (e: unknown) {
      clearWcSessions();
      throw e;
    } finally {
      removeListener?.();
    }
  }, [connectors, connectAsync]);

  const disconnect = useCallback(() => {
    disconnectAsync()
      .then(() => clearWcSessions())
      .catch(() => clearWcSessions());
  }, [disconnectAsync]);

  return Object.freeze({
    account: Object.freeze({ address, isConnected, isReconnecting, chainId, connectorId: connector?.id }),
    openModal,
    disconnect,
  });
};

// --- useSendTx: transaction sending via wagmi hooks ---

export const useSendTx = (): SendTxHookResult => {
  const config = useConfig();
  const { sendTransactionAsync } = useSendTransaction();
  const { writeContractAsync } = useWriteContract();
  const { switchChainAsync } = useSwitchChain();

  // Wallet I/O for the EVM senders. Chain/address are read at call time — the wallet may have changed since render
  const deps = useMemo(() => Object.freeze({
    sendTx: sendTransactionAsync as unknown as WagmiSendTx,
    writeContract: writeContractAsync as unknown as WagmiWriteContract,
    switchChain: switchChainAsync as unknown as (args: { readonly chainId: number }) => Promise<unknown>,
    getChainId: () => getConnection(config).chainId,
    waitForReceipt: ((args) => waitForTransactionReceipt(config, args)) as WaitForReceipt,
    readAllowance: (async ({ token, spender, chainId }) => {
      const owner = getConnection(config).address;
      if (owner === undefined) return 0n;
      return readContract(config, { address: token, abi: ERC20_ALLOWANCE_ABI, functionName: 'allowance', args: [owner, spender], chainId });
    }) as ReadAllowance,
  }), [config, sendTransactionAsync, writeContractAsync, switchChainAsync]);

  const sendTransaction = useCallback(async (request: TxRequest): Promise<TxResult> =>
    request.chainFamily === 'evm'
      ? sendEvmTransaction(request, deps)
      : err(`Non-EVM chain family "${request.chainFamily}" not supported by wagmi-latest adapter.`), [deps]);

  const approveFee = useCallback(async (request: TxRequest): Promise<TxResult> =>
    request.chainFamily === 'evm' ? approveEvmFeeSpend(request, deps) : ok('not-needed'), [deps]);

  return Object.freeze({ sendTransaction, approveFee });
};
